import { useState, useRef } from "react";
import useTheme from "./hooks/useTheme";
import ThemeToggle from "./components/ThemeToggle";
import {
  IGGGShell,
  IGGGInput,
  IGGGLabel,
  IGGGButton,
} from "./components/iggg/IGGGPrimitives";

// SHA256("ig1999")
const PASSWORD_HASH =
  "335c6b25331c5035295b4365c4f29ffd10817e10b59a90e49bede8b57a008cca";

async function sha256Hex(text) {
  const enc = new TextEncoder().encode(text);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export default function PasswordGate({ onSuccess }) {
  const { theme, toggleTheme } = useTheme();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !value) return;
    setSubmitting(true);
    setError("");
    try {
      const hash = await sha256Hex(value);
      if (hash === PASSWORD_HASH) {
        localStorage.setItem("iggg_auth", "true");
        onSuccess();
      } else {
        setError("ID 또는 비밀번호가 일치하지 않습니다");
        setShake(true);
        setValue("");
        setTimeout(() => setShake(false), 450);
        inputRef.current?.focus();
      }
    } catch {
      setError("인증 처리 오류");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <IGGGShell className="iggg-anim-fade">
      <style>{`
        @keyframes iggg-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(5px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(3px); }
        }
        .iggg-shake { animation: iggg-shake 0.45s ease; }
      `}</style>

      <div className="iggg-theme-pin">
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100dvh",
          padding: "32px 16px",
        }}
      >
        {/* 로고 영역 */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
            marginBottom: 32,
          }}
        >
          <img
            src="/iggg-logo.png"
            alt="iggg"
            data-testid="iggg-brand-logo"
            style={{
              height: "clamp(48px, 7vw, 72px)",
              width: "auto",
              objectFit: "contain",
              display: "block",
              filter: "var(--iggg-logo-filter, none)",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
          <span
            style={{
              fontFamily: "var(--ff-mono, JetBrains Mono, monospace)",
              fontSize: "0.8125rem",
              letterSpacing: "0.18em",
              color: "var(--iggg-fg-faint)",
              textTransform: "lowercase",
            }}
          >
            estimate
          </span>
        </div>

        {/* 로그인 박스 */}
        <form
          onSubmit={handleSubmit}
          className={shake ? "iggg-shake" : ""}
          style={{ width: "100%", maxWidth: 320 }}
        >
          <div style={{ marginBottom: 16 }}>
            <IGGGLabel sub="pw">비밀번호</IGGGLabel>
            <IGGGInput
              ref={inputRef}
              mono
              type="password"
              autoFocus
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError("");
              }}
              disabled={submitting}
              placeholder="••••••"
              autoComplete="current-password"
            />
          </div>

          <IGGGButton
            type="submit"
            variant="mono-primary"
            disabled={submitting || !value}
            style={{ width: "100%", padding: "12px 16px", fontSize: "0.8125rem" }}
          >
            {submitting ? "확인 중..." : "enter"}
          </IGGGButton>

          <div
            style={{
              fontFamily: "var(--ff-body)",
              fontSize: "0.75rem",
              color: "var(--iggg-danger)",
              textAlign: "center",
              marginTop: 12,
              minHeight: "1.2em",
              opacity: error ? 1 : 0,
              transition: "opacity 0.3s",
            }}
          >
            {error}
          </div>
        </form>

        <div
          style={{
            marginTop: 48,
            fontFamily: "var(--ff-mono)",
            fontSize: "0.625rem",
            letterSpacing: "0.18em",
            color: "var(--iggg-fg-faint)",
            textTransform: "uppercase",
          }}
        >
          iggg studio · internal use only
        </div>
      </div>
    </IGGGShell>
  );
}
